import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { RefreshCw, Search, Eye, Check, X, Trash2, Copy, MessageCircle, AlertCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardContent } from '../ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import { Label } from '../ui/label';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const statusBadge = (s) => {
  const map = {
    pending: 'bg-amber-100 text-amber-700 border-amber-300',
    resolved: 'bg-green-100 text-green-700 border-green-300',
    dismissed: 'bg-slate-100 text-slate-600 border-slate-300',
  };
  return map[s] || 'bg-slate-100 text-slate-600 border-slate-300';
};

const genPassword = (len = 10) => {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789@#';
  let pwd = '';
  for (let i = 0; i < len; i++) pwd += chars[Math.floor(Math.random() * chars.length)];
  return pwd;
};

const PasswordResetsManagement = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('pending');
  const [search, setSearch] = useState('');
  const [error, setError] = useState('');

  const [selected, setSelected] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [adminNote, setAdminNote] = useState('');
  const [resolveLoading, setResolveLoading] = useState(false);
  const [resolveResult, setResolveResult] = useState(null);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`${API}/admin/password-reset-requests`, {
        params: { status: filter },
      });
      setItems(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load reset requests');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const openResolve = (item) => {
    setSelected(item);
    setNewPassword(genPassword());
    setAdminNote('');
    setResolveResult(null);
  };

  const closeResolve = () => {
    setSelected(null);
    setNewPassword('');
    setAdminNote('');
    setResolveResult(null);
  };

  const submitResolve = async () => {
    if (!selected) return;
    if (!newPassword || newPassword.length < 6) {
      alert('Password must be at least 6 characters');
      return;
    }
    setResolveLoading(true);
    try {
      const res = await axios.post(
        `${API}/admin/password-reset-requests/${selected.id}/resolve`,
        { newPassword, note: adminNote }
      );
      setResolveResult({ ...res.data, password: newPassword });
      load();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to resolve request');
    } finally {
      setResolveLoading(false);
    }
  };

  const dismiss = async (id) => {
    if (!window.confirm('Dismiss this request without resetting password?')) return;
    try {
      await axios.post(`${API}/admin/password-reset-requests/${id}/dismiss`);
      load();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to dismiss');
    }
  };

  const removeItem = async (id) => {
    if (!window.confirm('Delete this request? This cannot be undone.')) return;
    try {
      await axios.delete(`${API}/admin/password-reset-requests/${id}`);
      load();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to delete');
    }
  };

  const copyText = (text) => {
    try {
      navigator.clipboard.writeText(text);
    } catch (e) {
      // noop
    }
  };

  const filtered = items.filter((it) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (
      (it.email || '').toLowerCase().includes(s) ||
      (it.userName || '').toLowerCase().includes(s) ||
      (it.userPhone || '').toLowerCase().includes(s) ||
      (it.id || '').toLowerCase().includes(s)
    );
  });

  return (
    <div className="space-y-4">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold text-slate-800">Password Reset Requests</h2>
          <p className="text-sm text-slate-500">
            Review user requests and set a new password. Share it with the user via WhatsApp.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex bg-slate-100 rounded-lg p-1">
            {['pending', 'resolved', 'dismissed', 'all'].map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`px-3 py-1.5 text-xs font-medium rounded-md capitalize ${
                  filter === f
                    ? 'bg-white text-blue-600 shadow-sm'
                    : 'text-slate-600 hover:text-slate-800'
                }`}
              >
                {f}
              </button>
            ))}
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={load}
            disabled={loading}
            className="border-slate-200"
          >
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      <div className="relative">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by email, name, phone or request ID..."
          className="pl-9 bg-white border-slate-200"
        />
      </div>

      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm flex items-center">
          <AlertCircle className="w-4 h-4 mr-2" />
          {error}
        </div>
      )}

      {loading ? (
        <div className="text-center py-10 text-slate-500">Loading...</div>
      ) : filtered.length === 0 ? (
        <Card className="bg-white border-slate-200">
          <CardContent className="py-12 text-center text-slate-500">
            No {filter !== 'all' ? filter : ''} password reset requests.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {filtered.map((it) => (
            <Card key={it.id} className="bg-white border-slate-200 hover:border-blue-300 transition-all">
              <CardContent className="p-4">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className="font-semibold text-slate-800">{it.userName || '(no name)'}</span>
                      <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full border ${statusBadge(it.status)}`}>
                        {it.status}
                      </span>
                      <span className="text-[10px] text-slate-400 font-mono">{it.id}</span>
                    </div>
                    <div className="text-sm text-slate-600 break-all">{it.email}</div>
                    {it.userPhone && (
                      <div className="text-xs text-slate-500">📞 {it.userPhone}</div>
                    )}
                    {it.message && (
                      <div className="text-xs text-slate-500 mt-1 italic">"{it.message}"</div>
                    )}
                    <div className="text-[11px] text-slate-400 mt-1">
                      Created: {it.createdAt ? new Date(it.createdAt).toLocaleString() : '-'}
                      {it.resolvedAt && ` • Resolved: ${new Date(it.resolvedAt).toLocaleString()}`}
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    {it.status === 'pending' && (
                      <>
                        <Button size="sm" onClick={() => openResolve(it)} className="bg-blue-600 hover:bg-blue-700 text-white">
                          <Check className="w-4 h-4 mr-1" /> Reset Password
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => dismiss(it.id)} className="border-slate-200">
                          <X className="w-4 h-4 mr-1" /> Dismiss
                        </Button>
                      </>
                    )}
                    {it.userPhone && (
                      <a
                        href={`https://wa.me/${(it.userPhone || '').replace(/\D/g, '')}`}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-green-200 text-green-700 hover:bg-green-50 text-xs font-medium"
                      >
                        <MessageCircle className="w-3.5 h-3.5" /> WhatsApp
                      </a>
                    )}
                    <Button size="sm" variant="ghost" onClick={() => removeItem(it.id)} className="text-red-500 hover:text-red-700">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Resolve Dialog */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && closeResolve()}>
        <DialogContent className="bg-white text-slate-800 border border-slate-200 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl text-blue-600">
              {resolveResult ? '✅ Password Reset Done' : 'Reset Password'}
            </DialogTitle>
          </DialogHeader>

          {!resolveResult ? (
            <div className="space-y-4">
              <div className="bg-slate-50 border border-slate-200 rounded-md p-3 text-sm">
                <div><strong>User:</strong> {selected?.userName || '-'}</div>
                <div><strong>Email:</strong> {selected?.email}</div>
                {selected?.userPhone && <div><strong>Phone:</strong> {selected.userPhone}</div>}
              </div>

              <div className="space-y-2">
                <Label className="text-slate-700 font-medium">New Password</Label>
                <div className="flex gap-2">
                  <Input
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="bg-white border-slate-200 font-mono"
                    placeholder="At least 6 characters"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setNewPassword(genPassword())}
                    className="border-slate-200"
                  >
                    Generate
                  </Button>
                </div>
                <p className="text-xs text-slate-500">Min 6 characters. User can change it after login.</p>
              </div>

              <div className="space-y-2">
                <Label className="text-slate-700 font-medium">Admin Note (optional)</Label>
                <Input
                  value={adminNote}
                  onChange={(e) => setAdminNote(e.target.value)}
                  placeholder="e.g. Verified via WhatsApp"
                  className="bg-white border-slate-200"
                />
              </div>

              <DialogFooter className="gap-2">
                <Button variant="outline" onClick={closeResolve} className="border-slate-200">Cancel</Button>
                <Button onClick={submitResolve} disabled={resolveLoading} className="bg-blue-600 hover:bg-blue-700 text-white">
                  {resolveLoading ? 'Resetting...' : 'Set New Password'}
                </Button>
              </DialogFooter>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="bg-green-50 border border-green-200 rounded-md p-3 text-sm text-green-800">
                Password reset successfully. Share the credentials below with the user.
              </div>
              <div className="bg-slate-50 border border-slate-200 rounded-md p-3 text-sm space-y-1">
                <div className="flex items-center justify-between gap-2">
                  <div><strong>Email:</strong> <span className="font-mono">{resolveResult.user?.email}</span></div>
                  <button onClick={() => copyText(resolveResult.user?.email || '')} className="text-blue-600 hover:underline text-xs flex items-center gap-1">
                    <Copy className="w-3 h-3" /> copy
                  </button>
                </div>
                <div className="flex items-center justify-between gap-2">
                  <div><strong>New Password:</strong> <span className="font-mono">{resolveResult.password}</span></div>
                  <button onClick={() => copyText(resolveResult.password || '')} className="text-blue-600 hover:underline text-xs flex items-center gap-1">
                    <Copy className="w-3 h-3" /> copy
                  </button>
                </div>
              </div>
              {resolveResult.user?.phone && (
                <a
                  href={`https://wa.me/${(resolveResult.user.phone || '').replace(/\D/g, '')}?text=${encodeURIComponent(`Hi ${resolveResult.user.name || ''},\n\nYour DropSquadz password has been reset.\n\nEmail: ${resolveResult.user.email}\nNew Password: ${resolveResult.password}\n\nLogin: https://dropsquadz.tech\n\nPlease change your password after logging in.`)}`}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 w-full justify-center px-4 py-2 rounded-md bg-green-600 hover:bg-green-700 text-white text-sm font-medium"
                >
                  <MessageCircle className="w-4 h-4" /> Send via WhatsApp
                </a>
              )}
              <DialogFooter>
                <Button onClick={closeResolve} className="bg-blue-600 hover:bg-blue-700 text-white w-full">Close</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PasswordResetsManagement;
